import React from 'react';
import Dialog from '@dashboard/components/Dialog';
import { Form } from '@dashboard/components/Formik';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import { apiKeysKey } from '@dashboard/lib/queryKeys';
import * as Yup from 'yup';

const ApiKeyDeleteDialog = ({ open, onClose, apiKey }) => {
  const { sellerId, token } = useAuth();
  const queryClient = useQueryClient();

  const deleteApiKey = useMutation({
    mutationFn: () =>
      fetcher(`/sellers/${sellerId}/api-keys/${apiKey?.id}`, token, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: apiKeysKey(sellerId) });
      toast({
        title: 'API key deleted',
        type: 'success',
      });
      onClose();
    },
    onError: () => {
      toast({
        title: 'Failed to delete API key',
        type: 'error',
      });
    },
  });

  return (
    <Dialog
      title="Delete API key"
      size="md"
      open={open}
      onCancel={onClose}
      cancelButton={null}
      confirmButton={null}
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-muted-foreground">
          Are you sure you want to delete the API key{' '}
          <span className="font-bold">{apiKey?.name}</span>? This action cannot be undone and will
          immediately invalidate the key.
        </p>

        {apiKey && (
          <div className="p-3 bg-gray-50 dark:bg-card rounded-md">
            <div className="text-sm">
              <span className="font-medium">Key preview:</span>{' '}
              <code className="text-sm bg-gray-100 dark:bg-muted px-1 py-0.5 rounded">
                {apiKey.keyPreview}
              </code>
            </div>
          </div>
        )}

        <Form
          showState={false}
          validationSchema={Yup.object().shape({
            apiKeyName: Yup.string()
              .required('API key name is required')
              .test(
                'matches-key-name',
                'The API key name entered does not match.',
                function (value) {
                  return value === apiKey?.name;
                },
              ),
          })}
          initialValues={{
            apiKeyName: '',
          }}
          onSubmit={async () => {
            try {
              await deleteApiKey.mutateAsync();
              return { success: true };
            } catch (error) {
              return { error };
            }
          }}
          fields={() => [
            {
              name: 'apiKeyName',
              label: (
                <>
                  Type <b>{apiKey?.name}</b> to confirm deletion
                </>
              ),
              type: 'text',
              required: true,
              placeholder: apiKey?.name,
            },
          ]}
          formSubmitAlign="end"
          submitButton={{
            label: 'Delete API key',
            appearance: 'secondary',
            disabled: deleteApiKey.isPending,
          }}
          cancelButton={{
            label: 'Cancel',
            appearance: 'secondary',
            onClick: onClose,
          }}
        />
      </div>
    </Dialog>
  );
};

export default ApiKeyDeleteDialog;
